/**
 * Address Tab Component
 *
 * Displays and edits correspondence address information.
 */

import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, AlertCircle, Pencil } from "lucide-react";
import type { User } from "@/services/authService";
import { authService } from "@/services/authService";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface AddressTabProps {
  user: User;
  allowEdit?: boolean;
}

function normalizeRtInput(raw: string) {
  return raw.replace(/\D/g, "").slice(0, 3);
}

function normalizeRwInput(raw: string) {
  return raw.replace(/\D/g, "").slice(0, 2);
}

function padRtDigits(digits: string): string {
  const d = normalizeRtInput(digits);
  if (!d) return "";
  return d.padStart(3, "0");
}

function padRwDigits(digits: string): string {
  const d = normalizeRwInput(digits);
  if (!d) return "";
  return d.padStart(2, "0");
}

function Field({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className={cn("p-4", className)}>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="text-sm font-medium text-foreground">{value || "-"}</div>
    </div>
  );
}

import { cn } from "@/lib/utils";

export function AddressTab({ user, allowEdit = true }: AddressTabProps) {
  const { refreshUser } = useAuth();
  const [isEditing, setIsEditing] = React.useState(false);
  const [alamat, setAlamat] = React.useState(user.alamat || "");
  const [rt, setRt] = React.useState(() => padRtDigits(user.rt || ""));
  const [rw, setRw] = React.useState(() => padRwDigits(user.rw || ""));
  const [kecamatan, setKecamatan] = React.useState(user.kecamatan || "");
  const [kelurahan, setKelurahan] = React.useState(user.kelurahan || "");
  const [kabupaten, setKabupaten] = React.useState(user.kabupaten || "");
  const [provinsi, setProvinsi] = React.useState(user.provinsi || "");
  const [kodePos, setKodePos] = React.useState(user.kode_pos || "");
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!isEditing) {
      setAlamat(user.alamat || "");
      setRt(padRtDigits(user.rt || ""));
      setRw(padRwDigits(user.rw || ""));
      setKecamatan(user.kecamatan || "");
      setKelurahan(user.kelurahan || "");
      setKabupaten(user.kabupaten || "");
      setProvinsi(user.provinsi || "");
      setKodePos(user.kode_pos || "");
    }
  }, [user.alamat, user.rt, user.rw, user.kecamatan, user.kelurahan, user.kabupaten, user.provinsi, user.kode_pos, isEditing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!user.alamat && !alamat.trim()) {
      setError("Alamat tidak boleh kosong");
      return;
    }

    const rtStored = padRtDigits(rt);
    const rwStored = padRwDigits(rw);

    setIsLoading(true);
    try {
      await authService.updateProfile({
        alamat: alamat.trim(),
        rt: rtStored || undefined,
        rw: rwStored || undefined,
        kecamatan: kecamatan.trim() || undefined,
        kelurahan: kelurahan.trim() || undefined,
        kabupaten: kabupaten.trim(),
        provinsi: provinsi.trim(),
        kode_pos: kodePos.trim() || undefined,
      });
      toast.success("Alamat berhasil diperbarui");
      await refreshUser();
      setIsEditing(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal memperbarui alamat";
      setError(errorMessage);
      toast.error("Gagal memperbarui alamat", { description: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setAlamat(user.alamat || "");
    setRt(padRtDigits(user.rt || ""));
    setRw(padRwDigits(user.rw || ""));
    setKecamatan(user.kecamatan || "");
    setKelurahan(user.kelurahan || "");
    setKabupaten(user.kabupaten || "");
    setProvinsi(user.provinsi || "");
    setKodePos(user.kode_pos || "");
    setError("");
    setIsEditing(false);
  };

  const unchanged =
    alamat === (user.alamat || "") &&
    padRtDigits(rt) === padRtDigits(user.rt || "") &&
    padRwDigits(rw) === padRwDigits(user.rw || "") &&
    kecamatan.trim() === (user.kecamatan || "").trim() &&
    kelurahan.trim() === (user.kelurahan || "").trim() &&
    kabupaten === (user.kabupaten || "") &&
    provinsi === (user.provinsi || "") &&
    kodePos === (user.kode_pos || "");

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-muted-foreground" />
          <CardTitle>Alamat Korespondensi</CardTitle>
        </div>
        {allowEdit && !isEditing && (
          <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} className="gap-1.5">
            <Pencil className="h-3.5 w-3.5" />
            Ubah
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {!isEditing ? (
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-border">
              <div className="text-xs text-muted-foreground mb-1">Alamat</div>
              <div className="text-sm font-medium text-foreground whitespace-pre-wrap">{user.alamat?.trim() || "-"}</div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4">
              <Field label="RT" value={padRtDigits(user.rt || "")} className="border-b sm:border-r border-border" />
              <Field label="RW" value={padRwDigits(user.rw || "")} className="border-b sm:border-r border-border lg:border-r" />
              <Field label="Kelurahan" value={user.kelurahan?.trim() || "-"} className="border-b sm:border-r border-border" />
              <Field label="Kecamatan" value={user.kecamatan?.trim() || "-"} className="border-b border-border" />
              <Field label="Kota / Kabupaten" value={user.kabupaten?.trim() || "-"} className="border-b sm:border-r border-border lg:border-r" />
              <Field label="Provinsi" value={user.provinsi?.trim() || "-"} className="border-b sm:border-r border-border" />
              <Field label="Kode Pos" value={user.kode_pos?.trim() || "-"} className="border-b border-border sm:border-r-0 lg:border-r" />
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="alamat">Alamat jalan</Label>
              <textarea
                id="alamat"
                className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={alamat}
                onChange={(e) => setAlamat(e.target.value)}
                placeholder="Contoh: Jl. Merdeka No. 1"
                disabled={isLoading}
                required
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="rt">RT (3 digit)</Label>
                <Input
                  id="rt"
                  value={rt}
                  onChange={(e) => setRt(normalizeRtInput(e.target.value))}
                  onBlur={() => setRt((v) => (normalizeRtInput(v) ? padRtDigits(v) : ""))}
                  placeholder="001"
                  disabled={isLoading}
                  inputMode="numeric"
                  maxLength={3}
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rw">RW (2 digit)</Label>
                <Input
                  id="rw"
                  value={rw}
                  onChange={(e) => setRw(normalizeRwInput(e.target.value))}
                  onBlur={() => setRw((v) => (normalizeRwInput(v) ? padRwDigits(v) : ""))}
                  placeholder="02"
                  disabled={isLoading}
                  inputMode="numeric"
                  maxLength={2}
                  className="font-mono"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="kelurahan">Kelurahan</Label>
                <Input id="kelurahan" value={kelurahan} onChange={(e) => setKelurahan(e.target.value)} placeholder="Nama kelurahan" disabled={isLoading} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="kecamatan">Kecamatan</Label>
                <Input id="kecamatan" value={kecamatan} onChange={(e) => setKecamatan(e.target.value)} placeholder="Nama kecamatan" disabled={isLoading} />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="kabupaten">Kota / Kabupaten</Label>
                <Input id="kabupaten" value={kabupaten} onChange={(e) => setKabupaten(e.target.value)} placeholder="Contoh: Kota Bandung" disabled={isLoading} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="provinsi">Provinsi</Label>
                <Input id="provinsi" value={provinsi} onChange={(e) => setProvinsi(e.target.value)} placeholder="Contoh: Jawa Barat" disabled={isLoading} />
              </div>
            </div>

            <div className="space-y-2 max-w-[200px]">
              <Label htmlFor="kode_pos">Kode Pos</Label>
              <Input
                id="kode_pos"
                type="text"
                inputMode="numeric"
                maxLength={5}
                value={kodePos}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "");
                  if (v.length <= 5) setKodePos(v);
                }}
                placeholder="12345"
                disabled={isLoading}
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-lg border border-destructive/20">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleCancel} disabled={isLoading}>
                Batal
              </Button>
              <Button type="submit" disabled={isLoading || unchanged || !alamat.trim()}>
                {isLoading ? "Menyimpan..." : "Simpan"}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
